import { useMutation, useQuery } from '@tanstack/react-query';
import { GenericQueryOptions } from 'queries/events/types';

import {
  ASSET_EXTERNAL_OIL_ANALYSIS,
  ASSET_EXTERNAL_VIBRATION_ANALYSIS,
  ASSET_EXTERNAL_SAP_NOTIFICATIONS,
  ASSET_EXTERNAL_MAINTENANCE_PLAN,
  ALARM_HISTORY,
} from 'config/endpoints';
import { get } from 'utils/axios';
import { dayjs } from 'lib/dayjs';
import { EDateFormats } from 'utils';
import { mockAvailableLinks } from 'mirage/externalReference.mock';
import {
  AlarmHistoryResponse,
  AvailableLinks,
  ExternalReferencesParams,
  MaintenancePlan,
  MaintenancePlanResponse,
  OilData,
  OilReponse,
  SapNote,
  SapNotesResponse,
  SaveAnalysisRequest,
  SaveAnalysisResponse,
  VibrationData,
  VibrationResponse,
  VibrationTable,
} from './contract';

export const buildDateRange = (period: string | number) =>
  period === 'max'
    ? { start_date: undefined, end_date: undefined }
    : {
        start_date: dayjs()
          .subtract(+period, 'days')
          .format(EDateFormats.API_DATE_FORMAT)
          .concat('T00:00:00'),
        end_date: dayjs().format(EDateFormats.API_DATE_FORMAT).concat('T00:00:00'),
      };

export const formatVibrationAnalysisResponse = (data: VibrationData[]): VibrationTable[] => {
  return data.reduce((acc: VibrationTable[], item: any) => {
    if (!acc.find((v) => v.id === item.tag)) {
      return [...acc, { id: item.tag, [item.month]: item }];
    }

    return acc.map((v) =>
      v.id === item.tag ? { ...v, [item.month]: item as VibrationData } : v,
    ) as VibrationTable[];
  }, [] as VibrationTable[]);
};

export const useAssetAvailableExternalReferencesQuery = (
  assetId?: string,
  queryOptions?: GenericQueryOptions<AvailableLinks>,
) =>
  useQuery(
    ['AVAILABLE_EXTERNAL_REFERENCE', assetId],
    async () => {
      // const response = await get<AvailableLinks>(ASSET_EXTERNAL_REFERENCES(assetId))();

      return mockAvailableLinks;
    },
    { ...queryOptions, retry: false },
  );

export const useSapNotesQuery = (
  assetId: string,
  period?: string,
  queryOptions?: GenericQueryOptions<SapNote[]>,
) => {
  const { start_date, end_date } = buildDateRange(period ?? '365');

  return useQuery(
    ['SAP_NOTES', assetId, period],
    async (): Promise<SapNote[]> => {
      const response = await get<SapNotesResponse, ExternalReferencesParams>(
        ASSET_EXTERNAL_SAP_NOTIFICATIONS(assetId),
        { start_date, end_date },
      )();

      return response.sap_notification;
    },
    {
      enabled: !!assetId,
      retry: false,
      ...queryOptions,
    },
  );
};
export const useVibrationAnalysisQuery = (
  assetId: string,
  period?: string,
  queryOptions?: GenericQueryOptions<VibrationTable[]>,
) => {
  const { start_date, end_date } = buildDateRange(period ?? '365');

  return useQuery(
    ['VIBRATION_ANALYSIS', 'MONITORING_LOADING', assetId, period],
    async () => {
      const response = await get<VibrationResponse, ExternalReferencesParams>(
        ASSET_EXTERNAL_VIBRATION_ANALYSIS(assetId),
        { start_date, end_date },
      )();

      return formatVibrationAnalysisResponse(response.vibration_analysis);
    },
    {
      enabled: !!assetId,
      retry: false,
      ...queryOptions,
    },
  );
};
export const useOilAnalysisQuery = (
  assetId: string,
  period?: string,
  queryOptions?: GenericQueryOptions<OilData[]>,
) => {
  const { start_date, end_date } = buildDateRange(period ?? '365');

  return useQuery(
    ['OIL_ANALYSIS', assetId, period],
    async () => {
      const response = await get<OilReponse, ExternalReferencesParams>(
        ASSET_EXTERNAL_OIL_ANALYSIS(assetId),
        { start_date, end_date },
      )();

      return response.oil_analysis;
    },
    {
      enabled: !!assetId,
      retry: false,
      ...queryOptions,
    },
  );
};

// Mocked API call - replace with real endpoint when ready
const saveOilAnalysisData = async (data: SaveAnalysisRequest): Promise<SaveAnalysisResponse> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return {
    success: true,
    message: `Successfully saved ${data.selectedCells.length} oil analysis evidence(s)`,
    savedCount: data.selectedCells.length,
  };
};

export const useOilAnalysisMutation = () => {
  return useMutation({
    mutationFn: saveOilAnalysisData,
    onSuccess: (data) => {
      console.log('Oil analysis data saved successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to save oil analysis data:', error);
    },
  });
};

// Mocked API call - replace with real endpoint when ready
const saveVibrationAnalysisData = async (
  data: SaveAnalysisRequest,
): Promise<SaveAnalysisResponse> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return {
    success: true,
    message: `Successfully saved ${data.selectedCells.length} vibration analysis evidence(s)`,
    savedCount: data.selectedCells.length,
  };
};

export const useVibrationAnalysisMutation = () => {
  return useMutation({
    mutationFn: saveVibrationAnalysisData,
    onSuccess: (data) => {
      console.log('Vibration analysis saved successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to save vibration analysis data:', error);
    },
  });
};

export const useMaintenancePlansQuery = (
  assetId: string,
  period?: string,
  queryOptions?: GenericQueryOptions<MaintenancePlan[]>,
) => {
  const { start_date, end_date } = buildDateRange(period ?? '365');

  return useQuery(
    ['MAINTENANCE_PLAN', assetId, start_date, end_date],
    async (): Promise<MaintenancePlan[]> => {
      const response = await get<MaintenancePlanResponse, ExternalReferencesParams>(
        ASSET_EXTERNAL_MAINTENANCE_PLAN(assetId),
        { start_date, end_date },
      )();
      return response.maintenance_items;
    },
    {
      enabled: !!assetId,
      retry: false,
      ...queryOptions,
    },
  );
};

export const useAlarmHistoryQuery = (
  model_name: string,
  period?: string,
  queryOptions?: GenericQueryOptions<AlarmHistoryResponse>,
) => {
  const { start_date, end_date } = buildDateRange(period ?? '30');
  return useQuery(
    ['ALARM_HISTORY', model_name, start_date, end_date],
    get<AlarmHistoryResponse, ExternalReferencesParams>(ALARM_HISTORY(model_name), {
      start_date,
      end_date,
    }),
    {
      retry: false,
      ...queryOptions,
    },
  );
};
